import { useState } from 'react';
import type { FormEvent } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { resetPassword as apiResetPassword } from '../api/auth.ts';
import { coerceErrorMessage } from '../components/Toast.tsx';
import { useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import { Button, FormField, Input, buttonClass, cx } from '../ui/index.ts';
import { AUTH_LINK_CLASS, AuthPageFrame } from './auth/AuthPageFrame.tsx';

/**
 * Password reset (email-link landing) — Field Notes rebuild (epic #950
 * PR-2, formerly the console-shared `createResetPassword` factory
 * instantiation). Reached via `/reset-password` and the legacy
 * `/password-reset` alias — both stay routed in App.tsx.
 *
 * Wire behavior preserved: reads `?token=`; a missing token disables
 * both inputs and the submit button and shows a persistent inline
 * banner; local min-length + confirm-match validation; success replaces
 * the form in place with a go-sign-in card. Errors render inline only.
 */
export function ResetPassword(): JSX.Element {
  const [search] = useSearchParams();
  const token = search.get('token');
  const { fetchImpl } = useSession();
  const t = useT();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const missingToken = token === null || token.length === 0;

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    if (missingToken) {
      setError(t('reset.error.missing_token'));
      return;
    }
    if (newPassword.length < 10) {
      setError(t('reset.error.password_too_short'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t('reset.error.password_mismatch'));
      return;
    }
    setSubmitting(true);
    try {
      await apiResetPassword({ token: token ?? '', newPassword }, fetchImpl);
      setSuccess(true);
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(coerceErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthPageFrame
      title={t('reset.subtitle')}
      description={t('reset.description')}
      footer={
        <Link to="/login" data-testid="reset-back-login" className={AUTH_LINK_CLASS}>
          {t('common.back_to_login')}
        </Link>
      }
    >
      {success ? (
        <div
          className="flex flex-col gap-3 rounded-lg border border-ok-line bg-raised p-5"
          data-testid="reset-success"
        >
          <h2 className="m-0 flex items-center gap-2 text-heading text-ink">
            <CheckCircle2 aria-hidden="true" className="h-4 w-4 shrink-0 text-ok" />
            {t('reset.success.title')}
          </h2>
          <p className="m-0 text-body text-ink-mid">{t('reset.success.body')}</p>
          <Link
            to="/login"
            data-testid="reset-login-link"
            className={cx(buttonClass('primary'), 'mt-1 w-fit')}
          >
            {t('reset.success.sign_in')}
          </Link>
        </div>
      ) : (
        <form className="flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
          <FormField label={t('reset.new_password')} fieldId="reset-new-password">
            <Input
              id="reset-new-password"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.currentTarget.value)}
              disabled={submitting || missingToken}
              required
              minLength={10}
              maxLength={128}
              data-testid="reset-new-password"
            />
          </FormField>
          <FormField label={t('reset.confirm_password')} fieldId="reset-confirm-password">
            <Input
              id="reset-confirm-password"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.currentTarget.value)}
              disabled={submitting || missingToken}
              required
              minLength={10}
              maxLength={128}
              data-testid="reset-confirm-password"
            />
          </FormField>
          {error !== null ? (
            <p className="m-0 text-label text-bad" role="alert" data-testid="reset-error">
              {error}
            </p>
          ) : null}
          {missingToken ? (
            <p className="m-0 text-label text-bad" role="alert" data-testid="reset-missing-token">
              {t('reset.error.token_banner')}
            </p>
          ) : null}
          <Button
            type="submit"
            variant="primary"
            loading={submitting}
            disabled={missingToken}
            data-testid="reset-submit"
            className="mt-1 h-9 w-full"
          >
            {submitting ? t('reset.submitting') : t('reset.submit')}
          </Button>
        </form>
      )}
    </AuthPageFrame>
  );
}
